/**
 * @param {HTMLElement} element
 * @returns {Record<string, unknown>}
 */
function buildSwiperConfig(element) {
  const hasMultipleSlides = element.dataset.multipleSlides === 'true';

  /** @type {Record<string, unknown>} */
  const config = {
    slidesPerView: 1,
    spaceBetween: 0,
    speed: Number(element.dataset.transitionSpeed) || 600,
    loop: false,
    rewind: hasMultipleSlides,
    watchOverflow: true,
    grabCursor: hasMultipleSlides,
    preloadImages: false,
    watchSlidesProgress: true,
    a11y: {
      enabled: true,
    },
  };

  if (element.dataset.autoplay === 'true') {
    config.autoplay = {
      delay: (Number(element.dataset.autoplayDelay) || 5) * 1000,
      disableOnInteraction: false,
      pauseOnMouseEnter: true,
      waitForTransition: true,
    };
  }

  const swiperElement = element.querySelector('.custom-hero-slider__swiper');
  if (!(swiperElement instanceof HTMLElement)) return config;

  const paginationElement = swiperElement.querySelector('.swiper-pagination');
  if (element.dataset.pagination === 'true' && paginationElement instanceof HTMLElement) {
    config.pagination = {
      el: paginationElement,
      clickable: true,
    };
  }

  const nextElement = swiperElement.querySelector('.swiper-button-next');
  const prevElement = swiperElement.querySelector('.swiper-button-prev');
  if (
    element.dataset.arrows === 'true' &&
    nextElement instanceof HTMLElement &&
    prevElement instanceof HTMLElement
  ) {
    config.navigation = {
      nextEl: nextElement,
      prevEl: prevElement,
    };
  }

  return config;
}

/**
 * @param {SwiperInstance} swiper
 * @param {HTMLElement} swiperElement
 * @param {HTMLElement} rootElement
 */
function syncAutoplayWithFirstSlide(swiper, swiperElement, rootElement) {
  if (elementAutoplayDisabled(rootElement)) return;
  if (!swiper.autoplay || typeof swiper.autoplay.stop !== 'function') return;

  const firstSlide = swiperElement.querySelector('.swiper-slide:first-child');
  if (!(firstSlide instanceof HTMLElement)) return;

  const firstImage = firstSlide.querySelector('img');
  const firstVideo = firstSlide.querySelector('video');

  const startAutoplay = function () {
    if (swiper.autoplay && typeof swiper.autoplay.start === 'function') {
      swiper.autoplay.start();
    }
  };

  if (firstVideo instanceof HTMLVideoElement) {
    if (firstVideo.readyState >= 2) return;
    swiper.autoplay.stop();
    firstVideo.addEventListener('loadeddata', startAutoplay, { once: true });
    firstVideo.addEventListener('error', startAutoplay, { once: true });
    return;
  }

  if (!(firstImage instanceof HTMLImageElement)) return;
  if (firstImage.complete && firstImage.naturalWidth > 0) return;

  swiper.autoplay.stop();
  firstImage.addEventListener('load', startAutoplay, { once: true });
  firstImage.addEventListener('error', startAutoplay, { once: true });
}

/**
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function elementAutoplayDisabled(element) {
  return element.dataset.autoplay !== 'true';
}

/**
 * @param {HTMLElement} element
 */
function destroySlider(element) {
  if (element.swiperInstance && typeof element.swiperInstance.destroy === 'function') {
    element.swiperInstance.destroy(true, true);
    element.swiperInstance = null;
  }
}

/**
 * @param {Element | Document} root
 */
function initCustomHeroSliders(root) {
  if (!(root instanceof Element || root instanceof Document)) return;
  if (typeof window.Swiper !== 'function') return;

  root.querySelectorAll('[data-custom-hero-slider]').forEach(function (element) {
    if (!(element instanceof HTMLElement)) return;

    const swiperElement = element.querySelector('.custom-hero-slider__swiper');
    if (!(swiperElement instanceof HTMLElement)) return;

    if (element.dataset.initialized === 'true') {
      destroySlider(element);
    }

    const SwiperCtor = window.Swiper;
    if (typeof SwiperCtor !== 'function') return;

    const swiper = new SwiperCtor(swiperElement, buildSwiperConfig(element));
    element.swiperInstance = swiper;
    element.dataset.initialized = 'true';

    if (element.dataset.heavyMedia === 'true') {
      syncAutoplayWithFirstSlide(swiper, swiperElement, element);
    }
  });
}

/**
 * @param {number} attempt
 */
function bootCustomHeroSliders(attempt) {
  if (typeof window.Swiper === 'function') {
    initCustomHeroSliders(document);
    return;
  }

  if (attempt >= 20) return;

  window.setTimeout(function () {
    bootCustomHeroSliders(attempt + 1);
  }, 100);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () {
    bootCustomHeroSliders(0);
  });
} else {
  bootCustomHeroSliders(0);
}

document.addEventListener('shopify:section:load', function (event) {
  if (!(event.target instanceof HTMLElement)) return;
  initCustomHeroSliders(event.target);
});

document.addEventListener('shopify:section:unload', function (event) {
  if (!(event.target instanceof HTMLElement)) return;

  event.target.querySelectorAll('[data-custom-hero-slider]').forEach(function (element) {
    if (element instanceof HTMLElement) {
      destroySlider(element);
      delete element.dataset.initialized;
    }
  });
});
